import { useGtm } from '@gtm-support/vue-gtm'
import type { Log } from '@scayle/storefront-nuxt'
import { useDebounceFn } from '@vueuse/core'

const WAIT_TIME = 1000

// TODO Handle tracking events when `gtm` is not initialized (server-side)
const handleNonInitializedTracking = (log: Log) => ({
  push: (data: any) => {
    log.warn(`Gtm was not initialized yet. Event: ${JSON.stringify(data)}`)
  },
})

export default defineNuxtPlugin((nuxtApp) => {
  const $gtm = useGtm()
  const log = useLog('tracking')

  if (!$gtm) {
    return {
      provide: {
        tracking: handleNonInitializedTracking(log),
        // Provided even here so callers can flush unconditionally rather than
        // guarding on a `$flushTracking` that the types say always exists.
        flushTracking: () => {},
      },
    }
  }

  type Push = typeof $gtm.push

  let lastIndex = -1
  const queue: Array<{ data: any; index: number }> = []

  const tracking: { push: Push } = {
    /**
     * Push data to GTM considering the "trackingEventOrder"
     */
    push: (data: any) => {
      const { trackingEventOrder } = useRuntimeConfig().public
      const index = data.event
        ? (trackingEventOrder.indexOf(data.event) ?? -1)
        : -1

      if (index === -1) {
        // Unlisted events sort next to the last listed one so they keep their
        // relative position. They must still schedule a flush: otherwise they
        // sit in the queue until some *other* event flushes it, and an unlisted
        // event that is the last action before the tab closes is only saved by
        // `beforeunload` — which mobile browsers routinely skip (ECOM-2851).
        queue.push({ data, index: lastIndex })
      } else {
        queue.push({ data, index })
        lastIndex = index
      }

      flushDebounced()
    },
  }

  const flush = () => {
    const sorted = queue.sort((a, b) => a.index - b.index)
    sorted.forEach((item) => {
      const { event, content_name: contentName, ...data } = item.data

      $gtm.push({
        event,
        ...(contentName && { content_name: contentName }),
        ...data,
      })
    })
    queue.length = 0
  }

  const flushDebounced = useDebounceFn(flush, WAIT_TIME)

  useEventListener('beforeunload', flush)

  nuxtApp.provide('tracking', tracking)
  nuxtApp.provide('flushTracking', flush)
})
